// eventActions.js
import { formatDateForICS, getIANATimeZone, escapeText, wrapLine } from '../utils/dateUtils';
import { DateTime } from 'luxon';
import ICAL from 'ical.js';
import { sendEventDetailEmail } from '../emails/emailService';

export const handleCopyLink = (url, showNotification) => {
  navigator.clipboard.writeText(url)
    .then(() => showNotification('Event link copied to clipboard!', 'success'))
    .catch(() => showNotification('Failed to copy link.', 'error'));
};

export const handleShareEmail = async (event, url, recipientEmail, recipientName = 'Gatherly User') => {
  try {
    const result = await sendEventDetailEmail(recipientEmail, recipientName, {
      ...event,
      url: url.split('/').pop() // Extract the event URL from the full URL
    });

    if (result.success) {
      return { success: true, message: 'Event details sent successfully' };
    } else {
      throw new Error(result.error || 'Failed to send email');
    }
  } catch (error) {
    console.error('Error sending event detail email:', error);
    return { success: false, error: error.message };
  }
};

export const generateICSLink = (event) => {
  if (!event) return null;
  
  const ianaTimeZone = event.timezone || DateTime.local().zoneName;
  
  const start = DateTime.fromFormat(`${event.startDate} ${event.startTime}`, 'yyyy-MM-dd HH:mm', { zone: ianaTimeZone });
  const end = DateTime.fromFormat(`${event.endDate} ${event.endTime}`, 'yyyy-MM-dd HH:mm', { zone: ianaTimeZone });
  
  if (!start.isValid || !end.isValid) {
    console.error('Invalid start or end date/time');
    return null;
  }
  
  const eventUrl = `${window.location.origin}/event/${event.url}`;
  
  // Convert HTML to formatted plain text for ICS
  const htmlToPlainText = (html) => {
    if (!html) return '';
    
    // Convert specific HTML elements to plain text with formatting
    let text = html
      // Blockquotes - wrap in quotes with line breaks
      .replace(/<blockquote>([\s\S]*?)<\/blockquote>/gi, (match, content) => {
        const cleanContent = content
          .replace(/<[^>]+>/g, '') // Strip inner tags
          .replace(/&nbsp;/gi, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        return `\n"${cleanContent}"\n`;
      })
      // Headings - add line breaks and emphasis
      .replace(/<h[1-6]>(.*?)<\/h[1-6]>/gi, '\n$1\n')
      // Paragraphs - add line breaks
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p>/gi, '')
      // Divs - add line breaks
      .replace(/<\/div>/gi, '\n')
      .replace(/<div>/gi, '')
      // List items - add bullets
      .replace(/<li>(.*?)<\/li>/gi, '• $1\n')
      .replace(/<\/ul>/gi, '\n')
      .replace(/<ul>/gi, '\n')
      .replace(/<\/ol>/gi, '\n')
      .replace(/<ol>/gi, '\n')
      // Line breaks
      .replace(/<br\s*\/?>/gi, '\n')
      // Links - show as "text (url)"
      .replace(/<a\s+[^>]*?href=["']([^"']+)["'][^>]*?>(.*?)<\/a>/gi, (match, url, text) => {
        return text && text !== url ? `${text} (${url})` : url;
      })
      // Remove remaining tags
      .replace(/<[^>]+>/g, '');
    
    // Use DOM to decode HTML entities
    const tmp = document.createElement('DIV');
    tmp.innerHTML = text;
    text = tmp.textContent || tmp.innerText || text;
    
    // Clean up excessive whitespace and nbsp
    text = text
      .replace(/\u00A0/g, ' ')     // Convert non-breaking spaces to regular spaces
      .replace(/[ \t]+$/gm, '')    // Remove trailing spaces on each line
      .replace(/\n{3,}/g, '\n\n')  // Max 2 line breaks
      .replace(/[ \t]{2,}/g, ' ')  // Collapse multiple spaces to one
      .trim();
    
    return text;
  };
  
  const description = event.description ? htmlToPlainText(event.description) : '';
  
  // Build a well-formatted description for the calendar
  let calendarDescription = '';
  
  if (event.hostName) {
    calendarDescription += `Host: ${event.hostName}\n`;
  }
  if (event.hostEmail) {
    calendarDescription += `Contact: ${event.hostEmail}\n`;
  }
  calendarDescription += `\nView full event details:\n${eventUrl}\n`;
  
  if (description) {
    calendarDescription += `\n━━━━━━━━━━━━━━━━━━━━\n\n${description}`;
  }
  
  const comp = new ICAL.Component(['vcalendar', [], []]);
  comp.updatePropertyWithValue('prodid', '-//Gatherly//Events//EN');
  comp.updatePropertyWithValue('version', '2.0');
  comp.updatePropertyWithValue('calscale', 'GREGORIAN');
  comp.updatePropertyWithValue('method', 'PUBLISH');

  // Add VTIMEZONE component
  const tzComp = new ICAL.Component('vtimezone');
  tzComp.addPropertyWithValue('tzid', ianaTimeZone);
  tzComp.addPropertyWithValue('x-lic-location', ianaTimeZone);

  const tzInfo = ICAL.TimezoneService.get(ianaTimeZone);
  if (tzInfo) {
    const currentYear = new Date().getFullYear();
    const transitions = tzInfo.vtimezone.getAllSubcomponents().filter(
      comp => comp.name === 'daylight' || comp.name === 'standard'
    );

    transitions.forEach(transition => {
      const subComp = new ICAL.Component(transition.name);
      const dtstart = transition.getFirstPropertyValue('dtstart');
      dtstart.year = currentYear;
      
      subComp.addPropertyWithValue('tzoffsetfrom', transition.getFirstPropertyValue('tzoffsetfrom'));
      subComp.addPropertyWithValue('tzoffsetto', transition.getFirstPropertyValue('tzoffsetto'));
      subComp.addPropertyWithValue('tzname', transition.getFirstPropertyValue('tzname'));
      subComp.addPropertyWithValue('dtstart', dtstart);
      
      if (transition.hasProperty('rrule')) {
        subComp.addPropertyWithValue('rrule', transition.getFirstPropertyValue('rrule'));
      }
      
      tzComp.addSubcomponent(subComp);
    });
  }

  comp.addSubcomponent(tzComp);

  const vevent = new ICAL.Component('vevent');
  const eventProp = new ICAL.Event(vevent);

  eventProp.summary = event.name;
  eventProp.description = calendarDescription;
  eventProp.location = event.location;
  eventProp.startDate = ICAL.Time.fromJSDate(start.toJSDate(), false);
  eventProp.endDate = ICAL.Time.fromJSDate(end.toJSDate(), false);
  
  // Set the timezone
  const timezone = new ICAL.Timezone({
    tzid: ianaTimeZone,
    component: tzComp
  });
  eventProp.startDate.zone = timezone;
  eventProp.endDate.zone = timezone;

  // Add UID, URL, and other properties
  vevent.addPropertyWithValue('uid', `${event.id}-${Date.now()}@gatherly.org`);
  vevent.addPropertyWithValue('url', eventUrl);
  vevent.addPropertyWithValue('status', 'CONFIRMED');
  vevent.addPropertyWithValue('sequence', '0');
  vevent.addPropertyWithValue('created', ICAL.Time.now());
  vevent.addPropertyWithValue('last-modified', ICAL.Time.now());
  vevent.addPropertyWithValue('dtstamp', ICAL.Time.now());

  // Add organizer if available
  if (event.hostName && event.hostEmail) {
    vevent.addPropertyWithValue('organizer', `mailto:${event.hostEmail}`, { cn: event.hostName });
  }

  comp.addSubcomponent(vevent);
  
  const icsContent = comp.toString();
  
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  return url;
};
